<template>
  <div>
    <!-- MOTD Modal Component -->
    <MotdModal :route="$route.path" />

    <div class="protected-page">
      <div class="welcome" :style="{ animationDelay: `${0}s` }">
        <i class="fas fa-cog settings-icon" @click="toggleSettingsPopup"></i>
        <span class="welcome-text">Pixie Apps</span>
        <button
          v-if="canOpenPixieDaily"
          type="button"
          class="pixie-daily-icon"
          title="pixie daily"
          aria-label="pixie daily"
          @click="showPixieDaily = true"
        >
          <i class="fas fa-file-alt" aria-hidden="true"></i>
        </button>
        <i
          class="fas fa-microphone microphone-icon"
          @click="startHomeMic"
        ></i>
        <span
          class="status-dot"
          :class="{
            'status-green': serviceAvailable,
            'status-red': !serviceAvailable,
          }"
        ></span>
      </div>

      <PixieDaily
        v-if="showPixieDaily && canOpenPixieDaily"
        @close="closePixieDaily"
      />

      <div
        v-if="showPixieDailyHint && roleLoaded && userRole < 3 && !showPixieDaily && !showVoiceRecorder"
        class="home-mic-hint pixie-daily-hint"
        role="note"
        :style="{
          '--home-mic-hint-top': homeMicHintTop,
          '--home-mic-arrow-right': pixieDailyArrowRight,
        }"
        @click="hidePixieDailyHint"
      >
        <div class="home-mic-hint-callout">
          <svg
            class="home-mic-hint-arrow"
            viewBox="0 0 40 72"
            aria-hidden="true"
          >
            <polygon points="4,28 20,2 36,28" fill="currentColor" />
            <line
              x1="20"
              y1="24"
              x2="20"
              y2="70"
              stroke="currentColor"
              stroke-width="4"
              stroke-linecap="round"
            />
          </svg>
          <p class="home-mic-hint-text">
            New feature! Pixie<br />
            Daily Summary.
          </p>
          <button
            type="button"
            class="home-mic-hint-dismiss"
            @click.stop="hidePixieDailyHintForever"
          >
            don't show this again
          </button>
        </div>
      </div>

      <div
        v-if="showHomeMicIphoneHint && !showPixieDailyHint && !showVoiceRecorder"
        class="home-mic-hint home-mic-iphone-hint"
        role="note"
        :style="{
          '--home-mic-hint-top': homeMicHintTop,
          '--home-mic-arrow-right': homeMicArrowRight,
        }"
        @click="hideHomeMicIphoneHint"
      >
        <div class="home-mic-hint-callout">
          <svg
            class="home-mic-hint-arrow"
            viewBox="0 0 40 72"
            aria-hidden="true"
          >
            <polygon points="4,28 20,2 36,28" fill="currentColor" />
            <line
              x1="20"
              y1="24"
              x2="20"
              y2="70"
              stroke="currentColor"
              stroke-width="4"
              stroke-linecap="round"
            />
          </svg>
          <p class="home-mic-hint-text">
            Voice memos are<br />
            now fixed for<br />
            iphone.
          </p>
          <button
            type="button"
            class="home-mic-hint-dismiss"
            @click.stop="hideHomeMicIphoneHintForever"
          >
            don't show this again
          </button>
        </div>
      </div>

      <div
        v-if="showHomeMicHint && !showHomeMicIphoneHint && !showPixieDailyHint && !showVoiceRecorder"
        class="home-mic-hint"
        role="note"
        :style="{
          '--home-mic-hint-top': homeMicHintTop,
          '--home-mic-arrow-right': homeMicArrowRight,
        }"
        @click="hideHomeMicHint"
      >
        <div class="home-mic-hint-callout">
          <svg
            class="home-mic-hint-arrow"
            viewBox="0 0 40 72"
            aria-hidden="true"
          >
            <polygon points="4,28 20,2 36,28" fill="currentColor" />
            <line
              x1="20"
              y1="24"
              x2="20"
              y2="70"
              stroke="currentColor"
              stroke-width="4"
              stroke-linecap="round"
            />
          </svg>
          <p class="home-mic-hint-text">
            The voice memo feature is new and improved! One tap starts
            recording, one tap sends it.
          </p>
          <button
            type="button"
            class="home-mic-hint-dismiss"
            @click.stop="hideHomeMicHintForever"
          >
            don't show this again
          </button>
        </div>
      </div>

      <div
        v-if="showVoiceRecorder"
        class="voice-mic-overlay"
        :class="{ 'voice-mic-overlay-office': homeMicOfficeOnly }"
      >
          <div class="voice-mic-stack">
            <div class="voice-mic-grid">
              <button
                type="button"
                class="voice-mic-pause"
                :aria-label="isRecordingPaused ? 'Resume' : 'Pause'"
                @click="toggleHomeMicPause"
              >
                <i
                  :class="
                    isRecordingPaused ? 'fas fa-microphone' : 'fas fa-pause'
                  "
                ></i>
                <span class="voice-mic-label">{{ isRecordingPaused ? 'resume' : 'pause' }}</span>
              </button>
              <button
                type="button"
                class="voice-mic-send"
                aria-label="Send"
                :disabled="sendingVoiceMemo"
                @click="sendHomeMicOverlay"
              >
                <i class="fas fa-paper-plane"></i>
                <span class="voice-mic-label">send</span>
              </button>
              <button
                type="button"
                class="voice-mic-delete"
                aria-label="Delete"
                @click="deleteHomeMicOverlay"
              >
                <i class="fas fa-trash"></i>
                <span class="voice-mic-label">delete</span>
              </button>
              <button
                type="button"
                class="voice-mic-exit"
                aria-label="Quit"
                @click="exitHomeMicOverlay"
              >
                <i class="fas fa-sign-out-alt"></i>
                <span class="voice-mic-label">quit</span>
              </button>
            </div>
            <button
              v-if="userRole < 3"
              type="button"
              class="voice-mic-office"
              :class="{ 'voice-mic-office-on': homeMicOfficeOnly }"
              :aria-pressed="homeMicOfficeOnly ? 'true' : 'false'"
              @click="homeMicOfficeOnly = !homeMicOfficeOnly"
            >
              Office only
            </button>
          </div>
        </div>

      <div class="content-container">
        <div class="tile-container scrollable">
          <!--
            Use div + router.push (not <a>/router-link). On some iOS versions,
            native anchor navigations from a Home Screen PWA open Safari with
            URL/toolbars instead of staying in standalone.
          -->
          <div
            class="tile"
            role="link"
            tabindex="0"
            :style="{ position: 'relative' }"
            @click="openTile('/mworkorders', 'Work Orders')"
            @keydown.enter.prevent="openTile('/mworkorders', 'Work Orders')"
          >
            <span v-if="unseenWorkOrdersCount > 0" class="work-orders-badge">{{
              unseenWorkOrdersCount
            }}</span>
            <div class="tile-icon"><i class="fas fa-hard-hat"></i></div>
            <div class="tile-link">Work Orders</div>
          </div>
          <div
            class="tile"
            role="link"
            tabindex="0"
            :style="{ animationDelay: `${1 * 0.05}s` }"
            @click="openTile('/mtenantsearch', 'Manage Tenants')"
            @keydown.enter.prevent="
              openTile('/mtenantsearch', 'Manage Tenants')
            "
          >
            <div class="tile-icon"><i class="fas fa-signature"></i></div>
            <div class="tile-link">Manage Tenants</div>
          </div>
          <div
            class="tile"
            role="link"
            tabindex="0"
            :style="{ animationDelay: `${2 * 0.05}s` }"
            @click="openTile('/mnotes', 'View All Notes')"
            @keydown.enter.prevent="openTile('/mnotes', 'View All Notes')"
          >
            <div class="tile-icon"><i class="fas fa-sticky-note"></i></div>
            <div class="tile-link">View All Notes</div>
          </div>
          <div
            class="tile"
            role="link"
            tabindex="0"
            :style="{ animationDelay: `${3 * 0.05}s`, position: 'relative' }"
            @click="openTile('/calendar', 'View Calendar')"
            @keydown.enter.prevent="openTile('/calendar', 'View Calendar')"
          >
            <span v-if="todayEventsCount > 0" class="calendar-badge">{{
              todayEventsCount
            }}</span>
            <div class="tile-icon">
              <i class="fas fa-calendar-alt"></i>
            </div>
            <div class="tile-link">View Calendar</div>
          </div>
          <div
            class="tile"
            role="link"
            tabindex="0"
            :style="{ animationDelay: `${4 * 0.05}s`, position: 'relative' }"
            @click="openTile('/ichat', 'Chat Bot')"
            @keydown.enter.prevent="openTile('/ichat', 'Chat Bot')"
          >
            <span v-if="unseenChatCount > 0" class="chat-bot-badge">{{
              unseenChatCount > 99 ? '99+' : unseenChatCount
            }}</span>
            <div class="tile-icon"><i class="fas fa-comment-dots"></i></div>
            <div class="tile-link">Chat Bot</div>
          </div>
          <div
            class="tile"
            role="link"
            tabindex="0"
            :style="{ animationDelay: `${5 * 0.05}s` }"
            @click="openTile('/appliances', 'Track Appliances')"
            @keydown.enter.prevent="openTile('/appliances', 'Track Appliances')"
          >
            <div class="tile-icon"><i class="fas fa-bathtub"></i></div>
            <div class="tile-link">Track Appliances</div>
          </div>
          <div
            class="tile"
            role="link"
            tabindex="0"
            :style="{ animationDelay: `${6 * 0.05}s` }"
            @click="openTile('/mproperties', 'Manage Properties')"
            @keydown.enter.prevent="
              openTile('/mproperties', 'Manage Properties')
            "
          >
            <div class="tile-icon"><i class="fas fa-school"></i></div>
            <div class="tile-link">Manage Properties</div>
          </div>
          <div
            v-if="userRole === 3"
            class="tile"
            role="link"
            tabindex="0"
            :style="{ animationDelay: `${7 * 0.05}s` }"
            @click="
              openTile('/mproperties?view=shop-inventory', 'Shop Inventory')
            "
            @keydown.enter.prevent="
              openTile('/mproperties?view=shop-inventory', 'Shop Inventory')
            "
          >
            <div class="tile-icon"><i class="fas fa-boxes"></i></div>
            <div class="tile-link">Shop Inventory</div>
          </div>
          <div
            v-if="userRole === 3"
            class="tile"
            role="button"
            tabindex="0"
            :style="{ animationDelay: `${8 * 0.05}s` }"
            @click="showTenantBroadcast = true"
            @keydown.enter.prevent="showTenantBroadcast = true"
          >
            <div class="tile-icon"><i class="fas fa-exclamation-triangle"></i></div>
            <div class="tile-link">Broadcast</div>
          </div>
          <div
            v-if="userRole <= 2"
            class="tile"
            role="link"
            tabindex="0"
            :style="{ animationDelay: `${7 * 0.05}s`, position: 'relative' }"
            @click="openTile('/msms', 'SMS Messaging')"
            @keydown.enter.prevent="openTile('/msms', 'SMS Messaging')"
          >
            <span v-if="staffSmsCount > 0" class="sms-badge">{{
              staffSmsCount > 99 ? '99+' : staffSmsCount
            }}</span>
            <div class="tile-icon"><i class="fas fa-sms"></i></div>
            <div class="tile-link">SMS Messaging</div>
          </div>
          <div
            v-if="userRole <= 2"
            class="tile"
            role="link"
            tabindex="0"
            :style="{ animationDelay: `${8 * 0.05}s`, position: 'relative' }"
            @click="openTile('/mvoice', 'Voice Messages')"
            @keydown.enter.prevent="openTile('/mvoice', 'Voice Messages')"
          >
            <span v-if="voiceMessagesCount > 0" class="sms-badge">{{
              voiceMessagesCount > 99 ? '99+' : voiceMessagesCount
            }}</span>
            <div class="tile-icon"><i class="fas fa-voicemail"></i></div>
            <div class="tile-link">Voice Messages</div>
          </div>
          <div
            v-if="userRole <= 2"
            class="tile"
            role="link"
            tabindex="0"
            :style="{ animationDelay: `${9 * 0.05}s`, position: 'relative' }"
            @click="openTile('/quickemail', 'Quick Email')"
            @keydown.enter.prevent="openTile('/quickemail', 'Quick Email')"
          >
            <span v-if="unseenEmailsCount > 0" class="email-badge">{{
              unseenEmailsCount
            }}</span>
            <div class="tile-icon"><i class="fas fa-envelope"></i></div>
            <div class="tile-link">Quick Email</div>
          </div>
          <div
            v-if="userRole <= 2"
            class="tile"
            role="link"
            tabindex="0"
            :style="{ animationDelay: `${10 * 0.05}s` }"
            @click="openTile('/quicklook', 'Bank and Ledger')"
            @keydown.enter.prevent="openTile('/quicklook', 'Bank and Ledger')"
          >
            <div class="tile-icon"><i class="fas fa-bank"></i></div>
            <div class="tile-link">Bank and Ledger</div>
          </div>
          <div
            class="tile"
            role="link"
            tabindex="0"
            :style="{ animationDelay: `${11 * 0.05}s` }"
            @click="logoutHome"
            @keydown.enter.prevent="logoutHome"
          >
            <div class="tile-icon"><i class="fas fa-frog"></i></div>
            <div class="tile-link">Bye, Fren! Logout</div>
          </div>
        </div>
      </div>

      <div class="chat-content" v-html="chatContent"></div>
      <div class="version-footer">
        <button
          class="version-btn"
          :disabled="updating"
          @click="checkForUpdate"
        >
          <i
            :class="updating ? 'fas fa-spinner fa-spin' : 'fas fa-sync-alt'"
          ></i>
          {{ updating ? 'Updating...' : 'Check for new version' }}
        </button>
      </div>

      <!-- Version update dialog (PixieChatBot-style) -->
      <div v-if="showUpdateDialog" class="update-overlay">
        <div class="update-card">
          <p class="update-message">
            A new version <strong>{{ availableVersion }}</strong> is available.
            Click install to proceed.
          </p>
          <p v-if="updateFailHint" class="update-fail-hint">
            {{ updateFailHint }}
          </p>
          <div class="update-actions">
            <button
              class="update-btn update-btn-primary"
              :disabled="updating"
              @click="installUpdate"
            >
              {{ updating ? 'Installing...' : 'Install' }}
            </button>
            <button
              class="update-btn update-btn-secondary"
              :disabled="updating"
              @click="cancelUpdate"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>

      <div
        v-if="showPushPrompt && !showUpdateDialog && !showUpdateWarningDialog"
        class="update-overlay"
      >
        <div class="update-card">
          <p class="update-message">
            Please enable push notifications so that you don't miss any
            messages.
          </p>
          <p
            v-if="notificationStatusError && notificationStatusMessage"
            class="update-fail-hint"
          >
            {{ notificationStatusMessage }}
          </p>
          <div class="update-actions">
            <button
              class="update-btn update-btn-primary"
              :disabled="notificationEnabling"
              @click="enablePushFromPrompt"
            >
              {{ notificationEnabling ? 'Enabling…' : 'Enable' }}
            </button>
            <button
              class="update-btn update-btn-secondary"
              :disabled="notificationEnabling"
              @click="showPushPrompt = false"
            >
              Close
            </button>
          </div>
        </div>
      </div>

      <div v-if="showUpdateWarningDialog" class="update-overlay">
        <div class="update-card">
          <p class="update-message">The app may not work as expected.</p>
          <button
            class="update-btn update-btn-primary update-btn-full"
            @click="dismissUpdateWarning"
          >
            OK
          </button>
        </div>
      </div>

      <!-- Log Message Display -->
      <div class="log-message" v-if="showDebug">
        <div class="log-message-content" v-html="logMessage"></div>
        <div class="button-container">
          <button @click="showAllData" class="data-button">
            show all data
          </button>
          <button @click="clearAllData" class="data-button">
            clear all data
          </button>
        </div>
      </div>

      <!-- Service Unavailable Popup -->
      <div v-if="showServicePopup" class="service-popup">
        <div class="service-popup-content">
          <p>Service is unavailable. Try again later.</p>
          <button
            @click="closeServicePopup"
            class="close-button settings-button"
          >
            Close
          </button>
        </div>
      </div>

      <!-- Settings Popup -->
      <div v-if="showSettingsPopup" class="settings-popup">
        <div
          class="settings-popup-content"
          :class="{ 'settings-popup-content--fluidbox': showFluidBoxWifi }"
        >
          <div class="settings-title-bar">
            <span class="settings-title">Settings</span>
            <button
              type="button"
              class="settings-close-btn"
              aria-label="Close"
              @click="closeSettingsPopup"
            >
              <i class="fas fa-times"></i>
            </button>
          </div>
          <div class="settings-body">
            <div v-if="!showFluidBoxWifi" class="user-info">
              <p>
                <span>{{ userDisplayName || 'Unknown User' }}</span>
              </p>
            </div>

            <div
              v-if="userRole === 1 && !showFluidBoxWifi"
              class="action-buttons-vertical"
            >
              <button @click="showMotdEditor = true" class="settings-button">
                Set MOTD
              </button>
              <button
                @click="toggleLogCapture"
                class="settings-button log-capture-button"
                :class="{ 'log-capture-active': logCaptureEnabled }"
              >
                <i
                  class="fas log-capture-icon"
                  :class="
                    logCaptureEnabled ? 'fa-stop-circle' : 'fa-dot-circle'
                  "
                  aria-hidden="true"
                ></i>
                <span>{{
                  logCaptureEnabled ? 'Stop Log Capture' : 'Start Log Capture'
                }}</span>
              </button>
              <button @click="forceUpdate()" class="settings-button">
                Force update
              </button>
              <button @click="fetchUsageInfo()" class="settings-button">
                Show usage info
              </button>
            </div>

            <div
              v-if="!showPasswordChange && !showMotdEditor && !showFluidBoxWifi"
              class="action-buttons-vertical"
            >
              <button
                @click="showPasswordChange = true"
                class="settings-button"
              >
                Change Password
              </button>
              <button
                @click="handleEnableNotifications"
                class="settings-button"
                :disabled="!notificationsSupported || notificationEnabling"
              >
                {{
                  !notificationsSupported
                    ? 'Notifications Unsupported'
                    : notificationEnabling
                      ? 'Enabling…'
                      : 'Enable Notifications'
                }}
              </button>
              <div class="notification-status" v-if="notificationsSupported">
                Status: {{ notificationPermissionLabel }}
              </div>
              <div
                v-if="notificationStatusMessage"
                class="password-message"
                :class="{ error: notificationStatusError }"
              >
                {{ notificationStatusMessage }}
              </div>
              <hr class="settings-divider" />
              <button
                type="button"
                class="settings-button"
                @click="showFluidBoxWifi = true"
              >
                Set up FluidBox Wi-Fi
              </button>
            </div>

            <FluidBoxWifiSetup
              v-if="showFluidBoxWifi"
              ref="fluidBoxWifiRef"
              @close="showFluidBoxWifi = false"
            />

            <div v-if="showPasswordChange" class="password-change-form">
              <div class="form-group">
                <label for="currentPassword">Current Password</label>
                <input
                  type="password"
                  id="currentPassword"
                  v-model="currentPassword"
                  placeholder="Enter current password"
                />
              </div>
              <div class="form-group">
                <label for="newPassword">New Password</label>
                <input
                  type="password"
                  id="newPassword"
                  v-model="newPassword"
                  placeholder="Enter new password"
                />
              </div>
              <div class="action-buttons">
                <button @click="changePassword" class="settings-button">
                  Submit
                </button>
                <button @click="cancelPasswordChange" class="cancel-button">
                  Cancel
                </button>
              </div>
              <div
                v-if="passwordMessage"
                class="password-message"
                :class="{ error: passwordError }"
              >
                {{ passwordMessage }}
              </div>
            </div>

            <!-- MOTD Editor -->
            <div v-if="showMotdEditor" class="motd-editor-form">
              <div class="form-group">
                <label for="motdRoute">Select Route</label>
                <select id="motdRoute" v-model="motdRoute">
                  <option
                    v-for="route in availableRoutes"
                    :key="route.path"
                    :value="route.path"
                  >
                    {{ route.name }}
                  </option>
                </select>
              </div>
              <div class="form-group">
                <label for="motdContent">Message Content (Markdown)</label>
                <textarea
                  id="motdContent"
                  v-model="motdContent"
                  placeholder="Enter message content using markdown"
                  rows="6"
                ></textarea>
              </div>

              <div class="preview-section">
                <label>Preview:</label>
                <div
                  class="markdown-preview"
                  v-html="renderedMotdPreview"
                ></div>
              </div>

              <div class="action-buttons">
                <button @click="saveMotd" class="settings-button">
                  Save MOTD
                </button>
                <button @click="cancelMotdEditor" class="cancel-button">
                  Cancel
                </button>
              </div>
              <div
                v-if="motdMessage"
                class="motd-message"
                :class="{ error: motdError }"
              >
                {{ motdMessage }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Usage Info Popup -->
      <div v-if="showUsageInfo" class="usage-info-popup">
        <div class="usage-info-popup-content">
          <h3>Recent Usage Info</h3>
          <div class="usage-entries-container">
            <table class="usage-table">
              <thead>
                <tr>
                  <th>Time</th>
                  <th>User</th>
                  <th>Action</th>
                  <th>Version</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="entry in usageEntries" :key="entry.id">
                  <td>{{ formatDate(entry.activity_timestamp) }}</td>
                  <td>{{ entry.displayName }}</td>
                  <td>{{ entry.usage_text }}</td>
                  <td>{{ entry.version || 'N/A' }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <button @click="closeUsageInfo" class="close-button">Close</button>
        </div>
      </div>
      <TenantBroadcastDialog
        v-if="showTenantBroadcast && userRole === 3"
        @close="showTenantBroadcast = false"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed, nextTick, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import axios from '@/axiosConfig';
import CryptoJS from 'crypto-js'; // Import CryptoJS for password hashing
import { marked } from 'marked'; // Import marked for rendering markdown
import { io } from 'socket.io-client';
import MotdModal from './MotdModal.vue'; // Import the MOTD modal component
import PixieDaily from './PixieDaily.vue';
import TenantBroadcastDialog from './TenantBroadcastDialog.vue';
import {
  pixieDailyAllowed,
  pixieDailyHintSeen,
  markPixieDailyHintSeen,
  pixieDailyHintArrowRight,
  pixieDailyRouteOpen,
  PIXIE_DAILY_QUERY,
} from '@/utils/pixieDailyHint';
import FluidBoxWifiSetup from './FluidBoxWifiSetup.vue';
import {
  enableNotifications,
  isStandalonePwa,
  notificationPermission,
  pushSupported,
  setChatAppBadge,
  subscribePushIfGranted,
} from '@/utils/chatPush';
import {
  persistPushEnabled,
  wasPushEnabled,
  pushStatusLabel,
  shouldKeepPushActive,
  shouldPromptStaffPush,
  rememberPushEnabledAfterPwaUpdate,
} from '@/utils/chatPushState';
import {
  applyPwaUpdate,
  clearUpdateAttemptTracking,
  fetchRemoteVersion,
  getFailedUpdateMessage,
  isPwaUpdateCooldown,
  versionUpdateOffer,
} from '@/utils/pwaUpdate';
import { shouldCountOnHomeBadge } from '@/utils/chatNotificationRules';
import { readDndAlwaysOn } from '@/utils/insideChatDnd';
import {
  smsAlertBadge,
  unreadAlwaysOnParam,
  publishDndAlwaysOn,
} from '@/utils/smsAlertQuiet';
import {
  HOME_MIC_START_DELAY_MS,
  allowAudioCapture,
  beginHomeMicAfterBeep,
  claimHomeVoiceMemoSend,
  isAudioSessionCaptureError,
  playHomeMicBeep,
  homeMicHintSeen,
  markHomeMicHintSeen,
  homeMicIphoneHintSeen,
  markHomeMicIphoneHintSeen,
} from '@/utils/homeVoiceMemo';
import { clearStaffSession } from '@/utils/staffLogout';
import { readNavigatorOnline } from '@/utils/networkBanner';
import {
  OUTBOUND_HOLD_TEXT,
  deliverOrQueue,
  isNetworkSendFailure,
} from '@/utils/outboundQueue';

const route = useRoute();
const router = useRouter();

const version = ref(process.env.VUE_APP_VERSION || 'dev');
const chatContent = ref('');
const showServicePopup = ref(false);
let serviceCheckInterval = null;
const serviceAvailable = ref(true); // Default to true until first check
const hostname = ref('');

// PWA / version update (ChatBot-style)
const updating = ref(false);
const showUpdateDialog = ref(false);
const showUpdateWarningDialog = ref(false);
const availableVersion = ref('');
const updateFailHint = ref('');

// Log message feature
const showDebug = ref(false);
const logMessage = ref('Initializing application...<br>');

const appendLog = (message, replacePrefix = null) => {
  if (replacePrefix) {
    // If replacePrefix is provided, find and replace existing line
    const regex = new RegExp(`${replacePrefix}[^<]*(<br>|$)`, 'i');
    if (regex.test(logMessage.value)) {
      // Replace the matching line with the new message
      logMessage.value = logMessage.value.replace(regex, message + '<br>');
      return;
    }
  }

  // Default behavior: append the message
  logMessage.value += message + '<br>';
};

const clearAllData = () => {
  const token = localStorage.getItem('token'); // Save the token

  // Get all localStorage keys
  const keys = Object.keys(localStorage);

  // Clear log message area
  logMessage.value = '';

  // Remove all items except token and log each removal
  for (const key of keys) {
    if (key !== 'token') {
      appendLog(`cleared ${key}`);
      localStorage.removeItem(key);
    }
  }

  // Re-save the token (if it existed)
  if (token) {
    localStorage.setItem('token', token);
  }
};

const showAllData = () => {
  // Get all localStorage keys
  const keys = Object.keys(localStorage);

  // Clear log message area
  logMessage.value = '';

  // Add header
  appendLog('=== localStorage Contents ===<br>');

  if (keys.length === 0 || (keys.length === 1 && keys[0] === 'token')) {
    appendLog('No data to display (other than auth token)');
    return;
  }

  // Display all items except token
  for (const key of keys) {
    if (key !== 'token') {
      let value = localStorage.getItem(key);

      // Try to format JSON data nicely if it's valid JSON
      try {
        const parsed = JSON.parse(value);
        value = JSON.stringify(parsed, null, 2);
      } catch (e) {
        // Not valid JSON, keep as is
      }

      appendLog(
        `<strong>${key}</strong>:<br><span style='color:#84ff84'>${value}</span><br>`,
      );
    }
  }
};

/**
 * Auto-check on home load (PixieChatBot pattern).
 * Only prompts when remote version is newer OR a waiting SW is ready.
 */
const checkForNewVersionAuto = async () => {
  if (process.env.NODE_ENV !== 'production') return;
  if (showUpdateDialog.value || updating.value) return;
  try {
    // Detect Install that reloaded but still served the old baked-in client
    const failed = getFailedUpdateMessage(version.value);
    if (failed) {
      availableVersion.value = failed.target;
      updateFailHint.value =
        `This device is still running ${failed.local} after Install ` +
        `(server has ${failed.target}). Usually the phone is holding a cached ` +
        `app shell, or the client build was not deployed with that version. ` +
        `Try Install again, or Settings → Force update. ` +
        `If it keeps failing, remove the home-screen icon and re-add it.`;
      showUpdateDialog.value = true;
      return;
    }

    if ('serviceWorker' in navigator) {
      const reg = await navigator.serviceWorker.getRegistration();
      if (reg) await reg.update();
    }

    const remoteVersion = await fetchRemoteVersion(axios);
    const offer = versionUpdateOffer({
      remoteVersion,
      localVersion: version.value,
    });

    if (offer.prompt) {
      availableVersion.value = offer.availableVersion;
      updateFailHint.value = '';
      showUpdateDialog.value = true;
    } else {
      clearUpdateAttemptTracking();
    }
  } catch (e) {
    // Best-effort on refresh; don't block the home screen.
    console.warn('[PWA] Auto version check failed:', e?.message || e);
  }
};

const installUpdate = async () => {
  if (updating.value) return;
  updating.value = true;
  let leftPage = false;
  const markLeft = () => {
    leftPage = true;
  };
  window.addEventListener('pagehide', markLeft);
  document.addEventListener('visibilitychange', markLeft);
  try {
    await applyPwaUpdate({
      targetVersion: availableVersion.value || undefined,
    });
    // location.href is async. Only treat as blocked if this document is
    // still visible after a long wait — the 5s flash was a false alarm.
    await new Promise((r) => setTimeout(r, 12000));
    if (!leftPage && document.visibilityState === 'visible') {
      updating.value = false;
      updateFailHint.value =
        'Reload was blocked. Close the app fully and reopen, or use Settings → Force update.';
    }
  } catch (e) {
    console.error('[PWA] Install update failed:', e);
    updating.value = false;
    window.location.reload();
  } finally {
    window.removeEventListener('pagehide', markLeft);
    document.removeEventListener('visibilitychange', markLeft);
  }
};

const cancelUpdate = () => {
  showUpdateDialog.value = false;
  showUpdateWarningDialog.value = true;
};

const dismissUpdateWarning = () => {
  showUpdateWarningDialog.value = false;
};

/**
 * Footer "Check for new version" — ChatBot-style:
 * if newer, show Install dialog; otherwise force-apply caches/SW and reload.
 */
const checkForUpdate = async () => {
  if (process.env.NODE_ENV !== 'production') {
    alert('Version checks only run in the production build.');
    return;
  }
  if (updating.value) return;
  updating.value = true;
  try {
    if ('serviceWorker' in navigator) {
      const reg = await navigator.serviceWorker.getRegistration();
      if (reg) await reg.update();
    }

    const remoteVersion = await fetchRemoteVersion(axios);
    const offer = versionUpdateOffer({
      remoteVersion,
      localVersion: version.value,
    });
    if (offer.prompt) {
      availableVersion.value = offer.availableVersion;
      updateFailHint.value = '';
      showUpdateDialog.value = true;
      return;
    }
    await applyPwaUpdate({
      targetVersion: remoteVersion || version.value,
    });
  } catch (e) {
    console.error('[PWA] Manual version check failed:', e);
    window.location.reload();
  } finally {
    updating.value = false;
  }
};

/** Admin settings: hard clear SW/caches and reload (same as install). */
const forceUpdate = async () => {
  showDebug.value = true;
  appendLog(
    'Force updating app (clear caches, reload icons, and load the latest version)...',
  );
  updating.value = true;
  try {
    let remote = null;
    try {
      remote = await fetchRemoteVersion(axios);
    } catch (_) {
      /* ignore */
    }
    await applyPwaUpdate({
      targetVersion: remote || availableVersion.value || version.value,
      purgeIcons: true,
    });
  } catch (error) {
    console.error('Force update failed:', error);
    appendLog(`Error during force update: ${error.message}`);
    updating.value = false;
    window.location.reload();
  }
};

// Service check function
const checkServiceAvailability = async () => {
  try {
    const response = await axios.get('/api/service-check');
    // Service is available, update status dot to green
    serviceAvailable.value = true;
    // Close the popup if it was shown
    if (showServicePopup.value) {
      showServicePopup.value = false;
    }

    // Store the hostname from the API response
    if (response.data && response.data.message) {
      hostname.value = response.data.message;

      // Update log message with hostname directly, without conditional check
      appendLog(`Hostname: ${response.data.message}<br>`, 'Hostname:');
    }
  } catch (error) {
    console.error('Service check failed:', error);
    if (isPwaUpdateCooldown()) {
      // Install just wiped the SW/cache; a 500 or network blip is expected.
      return;
    }
    serviceAvailable.value = false;
    showServicePopup.value = true;
  }
};

const closeServicePopup = () => {
  showServicePopup.value = false;
};

// Add PWA mobile touch handling specific to this component
const preventZoom = (e) => {
  if (e.touches && e.touches.length > 1) {
    e.preventDefault();
  }
};

const preventBounce = (e) => {
  // Prevent pull-to-refresh behavior on mobile
  const touchY = e.touches[0].clientY;
  const scrollTop = e.currentTarget.scrollTop;

  // At top and trying to pull down
  if (scrollTop === 0 && touchY > 0) {
    e.preventDefault();
  }

  // At bottom and trying to pull up
  const isAtBottom =
    e.currentTarget.scrollHeight - e.currentTarget.scrollTop ===
    e.currentTarget.clientHeight;
  if (isAtBottom && touchY < 0) {
    e.preventDefault();
  }
};

// Toggle debug log display
const toggleDebug = () => {
  showDebug.value = !showDebug.value;
};

// Settings popup feature
const showSettingsPopup = ref(false);
const showFluidBoxWifi = ref(false);
const fluidBoxWifiRef = ref(null);
const userDisplayName = ref('');
const userRole = ref(0); // Default user role
const roleLoaded = ref(false);
const showPixieDaily = ref(pixieDailyRouteOpen(route.query));
const showTenantBroadcast = ref(false);
watch(
  () => route.query[PIXIE_DAILY_QUERY],
  (value) => {
    if (String(value || '') === '1') showPixieDaily.value = true;
  },
);
const closePixieDaily = () => {
  showPixieDaily.value = false;
  if (!pixieDailyRouteOpen(route.query)) return;
  const query = { ...route.query };
  delete query[PIXIE_DAILY_QUERY];
  router.replace({ path: '/mhome', query });
};
const showPixieDailyHint = ref(false);
const pixieDailyArrowRight = ref(`${pixieDailyHintArrowRight()}px`);
const canOpenPixieDaily = computed(
  () => roleLoaded.value && pixieDailyAllowed(userRole.value),
);
const showPasswordChange = ref(false);
const currentPassword = ref('');
const newPassword = ref('');
const passwordMessage = ref('');
const passwordError = ref(false);

const showMotdEditor = ref(false);
const motdRoute = ref('');
const motdContent = ref('');
const motdMessage = ref('');
const motdError = ref(false);
const availableRoutes = ref([]);

const showUsageInfo = ref(false); // New state for usage info
const usageEntries = ref([]); // State for usage entries

// Notification settings state
const showPushPrompt = ref(false);
const notificationsSupported = ref(pushSupported());
const notificationPermissionState = ref(notificationPermission());
/** True only after push subscription is registered with the server */
const notificationPushActive = ref(false);
const notificationStatusMessage = ref('');
const notificationStatusError = ref(false);
const notificationEnabling = ref(false);

// Log capture state
const logCaptureEnabled = ref(false);

// Calendar badge
const todayEventsCount = ref(0);

// Work orders badge
const unseenWorkOrdersCount = ref(0);

// Quick Email badge
const unseenEmailsCount = ref(0);

// Chat Bot badge
const unseenChatCount = ref(0);
const staffSmsCount = ref(0);
const voiceMessagesCount = ref(0);
let chatBadgeSocket = null;
let dndBadgeTimer = null;
const processedChatBadgeMsgIds = new Set();

// Voice recorder state
const showVoiceRecorder = ref(false);
const homeMicOfficeOnly = ref(false);
const showHomeMicHint = ref(false);
const showHomeMicIphoneHint = ref(false);
const homeMicHintTop = ref('0px');
const homeMicArrowRight = ref('13px');
const isRecording = ref(false);
const isRecordingPaused = ref(false);
const audioData = ref(null);
const audioDataURL = ref(null);
const mediaRecorder = ref(null);
const mediaStream = ref(null); // Store the media stream to release it later
const chunks = ref([]);
const sendingVoiceMemo = ref(false);
let homeMicRun = 0;
let homeMicStarting = null;
let homeMicBeep = null;

// Format date for better readability in the usage info table
const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleString(); // Format to local date and time
};

// Function to fetch and show usage information
const fetchUsageInfo = async () => {
  try {
    const response = await axios.get('/api/usage-info');
    if (response.data) {
      usageEntries.value = response.data;
      showUsageInfo.value = true;
    }
  } catch (error) {
    console.error('Error fetching usage info:', error);
    appendLog(`Error fetching usage info: ${error.message}`);
  }
};

// Toggle log capture function
const toggleLogCapture = async () => {
  try {
    const action = logCaptureEnabled.value ? 'disable' : 'enable';
    const response = await axios.post('/api/toggle-log-capture', { action });

    if (response.data && response.data.success) {
      logCaptureEnabled.value = response.data.status.enabled;

      const message = logCaptureEnabled.value
        ? `Log capture started on ${response.data.status.hostname} (auto-stops in 10 min)`
        : `Log capture stopped on ${response.data.status.hostname}`;

      appendLog(message);
      // alert(message);
    } else {
      appendLog(response.data.message || 'Failed to toggle log capture');
      // alert(response.data.message || 'Failed to toggle log capture');
    }
  } catch (error) {
    console.error('Error toggling log capture:', error);
    appendLog(`Error toggling log capture: ${error.message}`);
    // alert('Error toggling log capture. Check console for details.');
  }
};

// Fetch log capture status on mount
const fetchLogCaptureStatus = async () => {
  if (userRole.value !== 1) return; // Only for admins

  try {
    const response = await axios.get('/api/log-capture-status');
    if (response.data) {
      logCaptureEnabled.value = response.data.enabled;
    }
  } catch (error) {
    // Silently fail - user might not be admin or endpoint not available
    console.debug('Could not fetch log capture status:', error.message);
  }
};

// Computed property for MOTD preview
const renderedMotdPreview = computed(() => {
  if (!motdContent.value) return '';
  return marked.parse(motdContent.value);
});

const notificationPermissionLabel = computed(() =>
  pushStatusLabel({
    pushActive: notificationPushActive.value,
    permission: notificationPermissionState.value,
    wasEnabled: wasPushEnabled(),
  }),
);

const refreshNotificationPushState = async () => {
  notificationPermissionState.value = notificationPermission();
  if (notificationPermissionState.value !== 'granted') {
    notificationPushActive.value = false;
    persistPushEnabled(false);
    return;
  }
  rememberPushEnabledAfterPwaUpdate({
    permission: notificationPermissionState.value,
    pwaUpdating: isPwaUpdateCooldown(),
  });
  // Don't flash "push not active" while the new SW resubscribes after Install.
  if (wasPushEnabled()) {
    notificationPushActive.value = true;
  }
  const restored = await subscribePushIfGranted();
  notificationPushActive.value = shouldKeepPushActive({
    restored,
    permission: notificationPermissionState.value,
    wasEnabled: wasPushEnabled() || restored,
  });
};

const onPushRestored = (event) => {
  if (!event || !event.detail || !event.detail.ok) return;
  notificationPushActive.value = true;
  persistPushEnabled(true);
};

const enablePushFromPrompt = async () => {
  await handleEnableNotifications();
  if (notificationPushActive.value) showPushPrompt.value = false;
};

const handleEnableNotifications = async () => {
  notificationStatusMessage.value = '';
  notificationStatusError.value = false;

  if (!notificationsSupported.value) {
    notificationStatusMessage.value =
      'Notifications are not supported on this device/browser.';
    notificationStatusError.value = true;
    return;
  }

  notificationEnabling.value = true;
  try {
    const result = await enableNotifications();
    notificationPermissionState.value =
      result.permission || notificationPermission();
    notificationPushActive.value = shouldKeepPushActive({
      restored: !!result.ok,
      permission: notificationPermissionState.value,
      wasEnabled: wasPushEnabled() || !!result.ok,
    });

    if (result.ok) {
      notificationStatusMessage.value =
        result.message || 'Notifications enabled for chat updates.';
      notificationStatusError.value = false;
      return;
    }

    // Permission may be granted even when push subscription fails — show the
    // real reason (service worker, VAPID, server, iOS standalone, etc.).
    notificationStatusMessage.value =
      result.message || 'Could not enable push notifications.';
    notificationStatusError.value = true;
  } finally {
    notificationEnabling.value = false;
  }
};

// Function to fetch available routes for MOTD dropdown
const fetchAvailableRoutes = async () => {
  try {
    const response = await axios.get('/api/motd/routes');
    if (response.data) {
      availableRoutes.value = response.data;
    }
  } catch (error) {
    console.error('Error fetching routes:', error);
  }
};

// Function to save MOTD
const saveMotd = async () => {
  if (!motdRoute.value || !motdContent.value) {
    motdMessage.value = 'Please fill out all fields.';
    motdError.value = true;
    return;
  }

  try {
    const response = await axios.post('/api/motd', {
      route: motdRoute.value,
      motd: motdContent.value,
    });

    if (response.status === 201) {
      motdMessage.value = 'Message of the day saved successfully.';
      motdError.value = false;

      // Clear the input fields
      setTimeout(() => {
        showMotdEditor.value = false;
        motdRoute.value = '';
        motdContent.value = '';
        motdMessage.value = '';
      }, 2000);
    }
  } catch (error) {
    console.error('Error saving MOTD:', error);
    motdMessage.value =
      error.response?.data?.error || 'Error saving message of the day.';
    motdError.value = true;
  }
};

const toggleSettingsPopup = () => {
  if (showSettingsPopup.value) {
    closeSettingsPopup();
  } else {
    showSettingsPopup.value = true;
    refreshNotificationPushState();
  }
};

const closeSettingsPopup = () => {
  if (fluidBoxWifiRef.value && fluidBoxWifiRef.value.abort) {
    fluidBoxWifiRef.value.abort({ notifyDevice: true });
  }
  showSettingsPopup.value = false;
  showFluidBoxWifi.value = false;
  showPasswordChange.value = false;
  showMotdEditor.value = false;
  currentPassword.value = '';
  newPassword.value = '';
  passwordMessage.value = '';
  passwordError.value = false;
  motdRoute.value = '';
  motdContent.value = '';
  motdMessage.value = '';
  motdError.value = false;
  renderedMotdPreview.value = '';
  notificationStatusMessage.value = '';
  notificationStatusError.value = false;
  notificationPermissionState.value = notificationPermission();
};

const cancelPasswordChange = () => {
  showPasswordChange.value = false;
  currentPassword.value = '';
  newPassword.value = '';
  passwordMessage.value = '';
  passwordError.value = false;
};

const changePassword = async () => {
  if (!currentPassword.value || !newPassword.value) {
    passwordMessage.value = 'Please fill out all fields.';
    passwordError.value = true;
    return;
  }

  try {
    // Create a timestamp to prevent replay attacks
    const timestamp = new Date().getTime();

    // Hash the passwords with SHA-256 (without appending timestamp)
    // This creates a consistent hash that can be bcrypted on the server
    const hashedCurrentPassword = CryptoJS.SHA256(
      currentPassword.value,
    ).toString();
    const hashedNewPassword = CryptoJS.SHA256(newPassword.value).toString();

    const response = await axios.post('/api/change-password', {
      currentPassword: hashedCurrentPassword,
      newPassword: hashedNewPassword,
      timestamp: timestamp, // Send timestamp for validation
    });

    if (response.data.success) {
      passwordMessage.value = 'Password changed successfully.';
      passwordError.value = false;
      // Clear sensitive data
      currentPassword.value = '';
      newPassword.value = '';
      setTimeout(() => {
        showPasswordChange.value = false;
        passwordMessage.value = '';
      }, 3000);
    } else {
      passwordMessage.value =
        response.data.message || 'Error changing password.';
      passwordError.value = true;
    }
  } catch (error) {
    console.error('Error changing password:', error);
    passwordMessage.value =
      error.response?.data?.message || 'Error changing password.';
    passwordError.value = true;
  }
};

const cancelMotdEditor = () => {
  showMotdEditor.value = false;
  motdRoute.value = '';
  motdContent.value = '';
  motdMessage.value = '';
  motdError.value = false;
  renderedMotdPreview.value = '';
};

const closeUsageInfo = () => {
  showUsageInfo.value = false;
};

const logTileClick = async (tileName) => {
  appendLog(`Tile clicked: ${tileName}`);

  try {
    // First get the user information from the user-info API
    const userResponse = await axios.get('/api/user-info');
    const userId = userResponse.data?.id || null;
    const displayName = userResponse.data?.displayName || null;

    // Send the usage data to the server with user information and version
    await axios.post('/api/log-usage', {
      usageText: tileName,
      userId: userId,
      displayName: displayName,
      version: version.value, // Include the version string defined in data
    });
  } catch (error) {
    console.error('Error logging tile click:', error);
  }
};

/** SPA-only navigation so iOS Home Screen PWAs stay in standalone mode. */
const openTile = (path, tileName) => {
  logTileClick(tileName);
  router.push(path);
};

const logoutHome = () => {
  logTileClick('Bye, Fren! Logout');
  const token = localStorage.getItem('token');
  if (token) {
    axios
      .post('/api/logout', null, {
        headers: { Authorization: `Bearer ${token}` },
        skipGlobalError: true,
        timeout: 8000,
      })
      .catch(() => {});
  }
  clearStaffSession(localStorage, sessionStorage);
  delete axios.defaults.headers.common['Authorization'];
  router.replace({ path: '/login', query: { loggedOut: '1' } });
};

// Fetch today's calendar events count for badge
const fetchTodayEventsCount = async () => {
  try {
    const response = await axios.get('/api/calendar/events');
    const events = response.data.data || [];
    const today = new Date();
    // Get today's date in local timezone (YYYY-MM-DD format)
    const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    console.log('Today string:', todayStr);
    console.log('Total events:', events.length);
    todayEventsCount.value = events.filter((event) => {
      // Compare date strings directly to avoid timezone conversion issues
      const eventDate = event.event_date
        ? event.event_date.split('T')[0]
        : null;
      console.log(
        'Event:',
        event.title,
        'Date:',
        eventDate,
        'Match:',
        eventDate === todayStr,
      );
      return eventDate === todayStr;
    }).length;
    console.log('Today events count:', todayEventsCount.value);
  } catch (error) {
    console.error("Error fetching today's events count:", error);
    todayEventsCount.value = 0;
  }
};

// Fetch unseen work orders count for badge
const fetchUnseenWorkOrdersCount = async () => {
  try {
    const maxId = localStorage.getItem('workOrderMaxId') || '0';
    const response = await axios.get('/api/work-orders/unseen-count', {
      params: { maxId },
    });
    unseenWorkOrdersCount.value = response.data.count || 0;
  } catch (error) {
    console.error('Error fetching unseen work orders count:', error);
    unseenWorkOrdersCount.value = 0;
  }
};

// Fetch unseen emails count for badge
const fetchUnseenEmailsCount = async () => {
  try {
    const lastEmailId = localStorage.getItem('lastEmailId') || '0';
    const response = await axios.get('/api/emails/unseen-count', {
      params: { lastEmailId },
    });
    unseenEmailsCount.value = response.data.count || 0;
  } catch (error) {
    console.error('Error fetching unseen emails count:', error);
    unseenEmailsCount.value = 0;
  }
};

// Handler for refreshing badges when page becomes visible
const handleVisibilityChange = () => {
  if (!document.hidden) {
    refreshAllBadges();
  }
};

const handleFocus = () => {
  refreshAllBadges();
};

// bfcache / SPA return: always re-sync badge totals from the server
const handlePageShow = () => {
  refreshAllBadges();
};

const fetchStaffSmsCount = async () => {
  try {
    const response = await axios.get('/api/checkUnreadState', {
      params: {
        live: 0,
        alwaysOn: unreadAlwaysOnParam(localStorage, userRole.value),
      },
    });
    staffSmsCount.value = Number(response.data.staffSms) || 0;
    voiceMessagesCount.value = Number(response.data.voiceMessages) || 0;
  } catch (error) {
    console.error('Error fetching staff SMS unread count:', error);
    staffSmsCount.value = 0;
    voiceMessagesCount.value = 0;
  }
};

const handleStaffSmsInbound = (payload) => {
  const alwaysOn = readDndAlwaysOn(localStorage, userRole.value);
  staffSmsCount.value = smsAlertBadge(payload, alwaysOn);
};

const handleVoiceMessagesInbound = (payload) => {
  voiceMessagesCount.value =
    Number(payload && payload.count) || voiceMessagesCount.value || 1;
};

const refreshAllBadges = () => {
  fetchUnseenEmailsCount();
  fetchUnseenWorkOrdersCount();
  fetchTodayEventsCount();
  fetchUnseenChatCount();
  fetchStaffSmsCount();
};

// Fetch unseen chat total for Chat Bot badge
const scheduleDndBadgeRefresh = (dnd) => {
  clearTimeout(dndBadgeTimer);
  dndBadgeTimer = null;
  const until = dnd && dnd.until;
  if (!until) return;
  const ts = new Date(until).getTime();
  if (Number.isNaN(ts) || ts <= Date.now()) return;
  const delay = Math.min(ts - Date.now() + 500, 2147483647);
  dndBadgeTimer = setTimeout(() => {
    fetchUnseenChatCount();
  }, delay);
};

const fetchUnseenChatCount = async () => {
  try {
    const response = await axios.get('/api/chat/unseen-count', {
      params: {
        alwaysOn: unreadAlwaysOnParam(localStorage, userRole.value),
      },
    });
    unseenChatCount.value = response.data.count || 0;
    setChatAppBadge(unseenChatCount.value);
    scheduleDndBadgeRefresh(response.data && response.data.dnd);
  } catch (error) {
    console.error('Error fetching unseen chat count:', error);
    unseenChatCount.value = 0;
    setChatAppBadge(0);
  }
};

function staffIdFromToken() {
  try {
    const token = localStorage.getItem('token');
    if (!token) return null;
    const payload = JSON.parse(atob(token.split('.')[1]));
    const raw = payload.sub ?? payload.id ?? payload.userId;
    return raw != null ? Number(raw) : null;
  } catch {
    return null;
  }
}

const handleChatBadgeSocketMessage = (data) => {
  if (!data) return;
  if (Array.isArray(data)) data = data[0];
  if (data.id != null) {
    if (processedChatBadgeMsgIds.has(data.id)) return;
    processedChatBadgeMsgIds.add(data.id);
    if (processedChatBadgeMsgIds.size > 100) {
      const oldest = processedChatBadgeMsgIds.values().next().value;
      processedChatBadgeMsgIds.delete(oldest);
    }
  }
  const me = staffIdFromToken();
  if (shouldCountOnHomeBadge(data, me)) fetchUnseenChatCount();
};

const handleTenantChatBadgeMessage = (data) => {
  if (!data) return;
  if (shouldCountOnHomeBadge(data, staffIdFromToken())) fetchUnseenChatCount();
};

const connectChatBadgeSocket = () => {
  try {
    const socketUrl = process.env.VUE_APP_SOCKET_URL;
    if (!socketUrl) return;
    const token = localStorage.getItem('token');
    if (chatBadgeSocket) {
      chatBadgeSocket.disconnect();
      chatBadgeSocket = null;
    }
    chatBadgeSocket = io(socketUrl, {
      query: { token, source: 'mHomePage' },
      transports: ['websocket', 'polling'],
      reconnection: true,
    });
    // `connect` also fires after reconnect — join staff_chat and refresh badge.
    chatBadgeSocket.on('connect', () => {
      if (token) chatBadgeSocket.emit('authenticate', token);
      fetchUnseenChatCount();
    });
    chatBadgeSocket.on('newChatMessages', handleChatBadgeSocketMessage);
    chatBadgeSocket.on('tenantChatMessage', handleTenantChatBadgeMessage);
    chatBadgeSocket.on('staffSmsInbound', handleStaffSmsInbound);
    chatBadgeSocket.on('voiceMessagesInbound', handleVoiceMessagesInbound);
  } catch (error) {
    console.error('MHomePage: chat badge socket connect failed:', error);
  }
};

const disconnectChatBadgeSocket = () => {
  if (!chatBadgeSocket) return;
  chatBadgeSocket.off('newChatMessages', handleChatBadgeSocketMessage);
  chatBadgeSocket.off('tenantChatMessage', handleTenantChatBadgeMessage);
  chatBadgeSocket.off('staffSmsInbound', handleStaffSmsInbound);
  chatBadgeSocket.off('voiceMessagesInbound', handleVoiceMessagesInbound);
  chatBadgeSocket.disconnect();
  chatBadgeSocket = null;
};

const cancelHomeMicBeep = () => {
  const beep = homeMicBeep;
  if (!beep || typeof beep.cancel !== 'function') return;
  beep.cancel();
};

const startHomeMic = () => {
  if (showVoiceRecorder.value) return homeMicStarting;
  const run = ++homeMicRun;
  showVoiceRecorder.value = true;
  homeMicOfficeOnly.value = false;
  isRecordingPaused.value = false;
  isRecording.value = false;
  chunks.value = [];
  audioData.value = null;
  homeMicStarting = runHomeMic(run);
  return homeMicStarting;
};

const runHomeMic = async (run) => {
  if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
    showVoiceRecorder.value = false;
    appendLog('getUserMedia not supported on this browser.');
    return;
  }

  const beep = playHomeMicBeep(undefined, HOME_MIC_START_DELAY_MS);
  homeMicBeep = beep;
  await beginHomeMicAfterBeep({
    beep,
    async startRecorder() {
      if (run !== homeMicRun || !showVoiceRecorder.value) return;
      let stream;
      try {
        await allowAudioCapture();
        stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      } catch (error) {
        if (isAudioSessionCaptureError(error)) {
          try {
            await allowAudioCapture('auto');
            stream = await navigator.mediaDevices.getUserMedia({ audio: true });
          } catch (retryError) {
            error = retryError;
          }
        }
        if (!stream) {
          console.error('Error accessing media devices:', error);
          if (run === homeMicRun) showVoiceRecorder.value = false;
          appendLog(`Error accessing media devices: ${error.message}`);
          return;
        }
      }

      if (run !== homeMicRun || !showVoiceRecorder.value) {
        stream.getTracks().forEach((track) => track.stop());
        return;
      }

      mediaStream.value = stream;
      try {
        mediaRecorder.value = new MediaRecorder(stream);
      } catch (error) {
        showVoiceRecorder.value = false;
        releaseMicrophone();
        appendLog(`Failed to create MediaRecorder: ${error.message}`);
        return;
      }

      mediaRecorder.value.ondataavailable = (e) => {
        if (e.data && e.data.size) chunks.value.push(e.data);
      };
      mediaRecorder.value.onstop = () => onRecordingStop();
      mediaRecorder.value.start();
      isRecording.value = true;
    },
  });
};

const toggleHomeMicPause = () => {
  const recorder = mediaRecorder.value;
  if (!recorder) return;
  if (recorder.state === 'recording' && typeof recorder.pause === 'function') {
    recorder.pause();
    isRecordingPaused.value = true;
    return;
  }
  if (recorder.state === 'paused' && typeof recorder.resume === 'function') {
    recorder.resume();
    isRecordingPaused.value = false;
  }
};

const finishHomeMicRecording = () => {
  const recorder = mediaRecorder.value;
  if (!recorder || recorder.state === 'inactive') return Promise.resolve();
  return new Promise((resolve) => {
    const previous = recorder.onstop;
    recorder.onstop = (event) => {
      if (typeof previous === 'function') previous(event);
      resolve();
    };
    try {
      recorder.stop();
    } catch (error) {
      resolve();
    }
  });
};

const onRecordingStop = () => {
  console.log('onRecordingStop called, chunks length:', chunks.value.length);
  const blob = new Blob(chunks.value, { type: 'audio/ogg; codecs=opus' });
  console.log('Created blob, size:', blob.size, 'bytes');
  audioData.value = blob;
  console.log('Set audioData:', audioData.value);
  chunks.value = [];

  audioDataURL.value = URL.createObjectURL(blob);
  console.log('Created audioDataURL:', audioDataURL.value);
};

const deleteRecording = () => {
  audioData.value = null;
  if (audioDataURL.value) {
    URL.revokeObjectURL(audioDataURL.value);
    audioDataURL.value = null;
  }
};

// Release the microphone stream
const releaseMicrophone = () => {
  if (mediaStream.value) {
    console.log('Releasing microphone - stopping all tracks');
    mediaStream.value.getTracks().forEach((track) => {
      track.stop();
      console.log('Stopped track:', track.kind, track.label);
    });
    mediaStream.value = null;
  }

  // Also clear the media recorder
  if (mediaRecorder.value) {
    mediaRecorder.value = null;
  }
};

const sendRecording = async () => {
  if (
    !claimHomeVoiceMemoSend({
      sending: sendingVoiceMemo.value,
      hasAudio: !!audioData.value,
    })
  ) {
    if (!audioData.value) appendLog('No audio data to send');
    return;
  }
  sendingVoiceMemo.value = true;
  const recording = audioData.value;
  const officeOnly = !!homeMicOfficeOnly.value;

  try {
    let online = readNavigatorOnline();
    let displayName = userDisplayName.value || 'User';
    if (online) {
      try {
        const userResponse = await axios.get('/api/user-info', {
          skipGlobalError: true,
        });
        displayName = userResponse.data?.displayName || displayName;
      } catch (error) {
        if (!isNetworkSendFailure(error, online)) throw error;
        online = false;
      }
    }

    // Extract first name from displayName (split on space, take first token)
    const firstName = String(displayName).split(' ')[0] || 'User';

    // Create timestamp in format "11/10/25 at 3:30pm"
    const now = new Date();
    const month = now.getMonth() + 1;
    const day = now.getDate();
    const year = String(now.getFullYear()).slice(-2);
    const hours = now.getHours();
    const minutes = now.getMinutes();
    const ampm = hours >= 12 ? 'pm' : 'am';
    const hour12 = hours % 12 || 12;
    const minutesPadded = String(minutes).padStart(2, '0');

    const timestamp = `${month}/${day}/${year} at ${hour12}:${minutesPadded}${ampm}`;
    const title = `From ${firstName} ${timestamp}`;

    appendLog(`Creating voice memo: ${title}`);

    const outcome = await deliverOrQueue({
      online,
      item: {
        kind: 'voice-note',
        title,
        filename: 'recording.ogg',
        attachedTo: officeOnly ? '6' : null,
        audio: recording,
      },
      send: async () => {
        const formData = new FormData();
        formData.append('audioBlob', recording, 'recording.ogg');
        formData.append('title', title);
        // Home memos stay unattached unless this take is office-only (attachedTo 6).
        if (officeOnly) {
          formData.append('attachedTo', '6');
        }
        return axios.post('/api/saveVoiceNote', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          skipGlobalError: true,
        });
      },
    });

    if (outcome.queued) {
      appendLog(OUTBOUND_HOLD_TEXT);
      showVoiceRecorder.value = false;
      homeMicOfficeOnly.value = false;
      deleteRecording();
      releaseMicrophone();
    } else if (outcome.result && outcome.result.status === 200) {
      appendLog('Voice memo sent successfully');
      showVoiceRecorder.value = false;
      homeMicOfficeOnly.value = false;
      deleteRecording();
      releaseMicrophone(); // Release the microphone after sending
    } else {
      const response = outcome.result;
      appendLog(`Unexpected response: ${response ? response.status : 'none'}`);
    }
  } catch (error) {
    console.error('Error sending audio recording:', error);
    appendLog(`Error: ${error.message || 'Unknown error sending recording'}`);
  } finally {
    sendingVoiceMemo.value = false;
  }
};

const armHomeMicRecorder = (stream) => {
  const recorder = new MediaRecorder(stream);
  recorder.ondataavailable = (e) => {
    if (e.data && e.data.size) chunks.value.push(e.data);
  };
  recorder.onstop = () => onRecordingStop();
  mediaRecorder.value = recorder;
  return recorder;
};

const deleteHomeMicOverlay = async () => {
  cancelHomeMicBeep();
  const run = ++homeMicRun;
  if (homeMicStarting) {
    try {
      await homeMicStarting;
    } catch (error) {
      appendLog(`Error starting microphone: ${error.message}`);
    }
  }
  if (run !== homeMicRun || !showVoiceRecorder.value) return;
  await finishHomeMicRecording();
  deleteRecording();
  chunks.value = [];
  isRecording.value = false;
  isRecordingPaused.value = true;

  let stream = mediaStream.value;
  const openedHere = !stream;
  if (!stream) {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      appendLog('getUserMedia not supported on this browser.');
      return;
    }
    try {
      await allowAudioCapture();
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (error) {
      if (!isAudioSessionCaptureError(error)) {
        appendLog(`Error accessing media devices: ${error.message}`);
        return;
      }
      try {
        await allowAudioCapture('auto');
        stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      } catch (retryError) {
        appendLog(`Error accessing media devices: ${retryError.message}`);
        return;
      }
    }
  }
  if (run !== homeMicRun || !showVoiceRecorder.value) {
    if (openedHere) stream.getTracks().forEach((track) => track.stop());
    return;
  }
  mediaStream.value = stream;
  let recorder;
  try {
    recorder = armHomeMicRecorder(stream);
    recorder.start();
    if (typeof recorder.pause === 'function') recorder.pause();
  } catch (error) {
    appendLog(`Failed to restart recording: ${error.message}`);
    return;
  }
  isRecording.value = true;
  isRecordingPaused.value = true;
};

const exitHomeMicOverlay = async () => {
  cancelHomeMicBeep();
  homeMicRun += 1;
  showVoiceRecorder.value = false;
  homeMicOfficeOnly.value = false;
  if (homeMicStarting) {
    try {
      await homeMicStarting;
    } catch (error) {
      appendLog(`Error starting microphone: ${error.message}`);
    }
  }
  await finishHomeMicRecording();
  deleteRecording();
  chunks.value = [];
  isRecording.value = false;
  isRecordingPaused.value = false;
  releaseMicrophone();
};

const sendHomeMicOverlay = async () => {
  cancelHomeMicBeep();
  if (homeMicStarting) {
    try {
      await homeMicStarting;
    } catch (error) {
      appendLog(`Error starting microphone: ${error.message}`);
    }
  }
  await finishHomeMicRecording();
  isRecording.value = false;
  isRecordingPaused.value = false;
  showVoiceRecorder.value = false;
  await sendRecording();
};

const hideHomeMicHint = () => {
  showHomeMicHint.value = false;
};

const hideHomeMicHintForever = () => {
  showHomeMicHint.value = false;
  markHomeMicHintSeen(localStorage);
};

const hideHomeMicIphoneHint = () => {
  showHomeMicIphoneHint.value = false;
};

const hideHomeMicIphoneHintForever = () => {
  showHomeMicIphoneHint.value = false;
  markHomeMicIphoneHintSeen(localStorage);
};

const hidePixieDailyHint = () => {
  showPixieDailyHint.value = false;
};

const hidePixieDailyHintForever = () => {
  showPixieDailyHint.value = false;
  markPixieDailyHintSeen(localStorage);
};

const placePixieDailyHint = () => {
  const page = document.querySelector('.protected-page');
  const welcome = document.querySelector('.welcome');
  const icon = document.querySelector('.pixie-daily-icon');
  if (welcome) homeMicHintTop.value = `${welcome.offsetHeight}px`;
  if (!page || !icon) return false;
  const pageBox = page.getBoundingClientRect();
  const box = icon.getBoundingClientRect();
  if (box.width < 1) return false;
  const centerFromRight = pageBox.right - (box.left + box.width / 2);
  pixieDailyArrowRight.value = `${Math.round(centerFromRight - 20)}px`;
  return true;
};

const schedulePixieDailyHint = () => {
  nextTick(() => {
    if (placePixieDailyHint()) return;
    requestAnimationFrame(() => placePixieDailyHint());
  });
};

onMounted(() => {
  document.title = 'Mobile Home';
  const page = document.querySelector('.protected-page');
  const welcome = document.querySelector('.welcome');
  const mic = document.querySelector('.microphone-icon');
  if (welcome) homeMicHintTop.value = `${welcome.offsetHeight}px`;
  if (page && mic) {
    const pageBox = page.getBoundingClientRect();
    const micBox = mic.getBoundingClientRect();
    const centerFromRight = pageBox.right - (micBox.left + micBox.width / 2);
    homeMicArrowRight.value = `${Math.round(centerFromRight - 20)}px`;
  }
  showHomeMicHint.value = !homeMicHintSeen(localStorage);
  showHomeMicIphoneHint.value = !homeMicIphoneHintSeen(localStorage);

  // Apply additional PWA touch handlers for this specific view
  const container = document.querySelector('.tile-container');
  if (container) {
    // Prevent pinch-zoom
    container.addEventListener('touchstart', preventZoom, { passive: false });

    // Prevent bounce/overscroll effects
    container.addEventListener('touchmove', preventBounce, { passive: false });
  }

  // Auto-detect newer client / waiting SW (ChatBot-style popup)
  checkForNewVersionAuto();

  // Initial service check — delay after Install so we do not race the
  // service-check rate limit or an empty service-worker cache.
  if (isPwaUpdateCooldown()) {
    setTimeout(checkServiceAvailability, 2500);
  } else {
    checkServiceAvailability();
  }

  // Set up interval for service check every minute
  serviceCheckInterval = setInterval(checkServiceAvailability, 60000);

  // Fetch calendar events count for badge
  fetchTodayEventsCount();

  // Fetch unseen work orders count for badge
  fetchUnseenWorkOrdersCount();

  // Fetch unseen emails count for badge
  fetchUnseenEmailsCount();
  fetchStaffSmsCount();

  // Fetch unseen chat count for Chat Bot badge
  fetchUnseenChatCount();

  // Live badge updates (direct, group, system, tenant) while home is open
  connectChatBadgeSocket();

  // Refresh badge counts when page becomes visible again (e.g., navigating back from chat)
  document.addEventListener('visibilitychange', handleVisibilityChange);
  window.addEventListener('focus', handleFocus);
  window.addEventListener('pageshow', handlePageShow);

  // Add status info to log (shown when tapping "Pixie Apps")
  // Hostname will be added in the checkServiceAvailability function after API response
  const deviceWidth = window.innerWidth;
  const deviceHeight = window.innerHeight;

  appendLog(`<br>Version: ${version.value}`);
  appendLog(`Device Dimensions: ${deviceWidth}x${deviceHeight}`);

  // Add click listener to title to toggle debug view
  const welcomeElement = document.querySelector('.welcome-text');
  if (welcomeElement) {
    welcomeElement.addEventListener('click', toggleDebug);
  }

  // Fetch user display name and role
  axios.get('/api/user-info').then((response) => {
    if (response.data) {
      if (response.data.displayName) {
        userDisplayName.value = response.data.displayName;
      }
      if (response.data.role !== undefined) {
        userRole.value = response.data.role;
        roleLoaded.value = true;
        if (!pixieDailyAllowed(userRole.value)) {
          showPixieDaily.value = false;
          showPixieDailyHint.value = false;
        } else if (!pixieDailyHintSeen(localStorage)) {
          showPixieDailyHint.value = true;
          schedulePixieDailyHint();
        }
        publishDndAlwaysOn(readDndAlwaysOn(localStorage, userRole.value));
        fetchStaffSmsCount();
        // Fetch log capture status if admin
        if (response.data.role === 1) {
          fetchLogCaptureStatus();
        }
      }
    }
  });

  // Fetch available routes for MOTD
  fetchAvailableRoutes();

  // Refresh permission state in case user changed OS/app notification settings.
  // Also recreate the push subscription if a deploy/Install dropped it.
  window.addEventListener('pixie-push-restored', onPushRestored);
  rememberPushEnabledAfterPwaUpdate({
    permission: notificationPermission(),
    pwaUpdating: isPwaUpdateCooldown(),
  });
  if (wasPushEnabled() && notificationPermission() === 'granted') {
    notificationPushActive.value = true;
  }
  refreshNotificationPushState();
  showPushPrompt.value = shouldPromptStaffPush({
    supported: notificationsSupported.value,
    permission: notificationPermission(),
  });
  // Hint for iPhone users who opened the site in Safari instead of the PWA
  if (
    notificationsSupported.value &&
    notificationPermissionState.value !== 'granted' &&
    /iPad|iPhone|iPod/.test(navigator.userAgent || '') &&
    !isStandalonePwa()
  ) {
    notificationStatusMessage.value =
      'On iPhone, open Pixie from the Home Screen app icon to enable notifications.';
    notificationStatusError.value = false;
  }

  // Fetch usage info
  axios.get('/api/usage-info').then((response) => {
    if (response.data) {
      usageEntries.value = response.data;
    }
  });

  // Log user location
  if (navigator.geolocation) {
    navigator.geolocation.getCurrentPosition(
      (position) => {
        axios.post('/api/log-location', {
          componentName: 'MHomePage',
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
        });
      },
      (error) => {
        axios.post('/api/log-location', {
          componentName: 'MHomePage',
          locationError: error.message,
        });
      },
    );
  }
});

onUnmounted(() => {
  // Clear the interval when component is unmounted
  if (serviceCheckInterval) {
    clearInterval(serviceCheckInterval);
  }
  clearTimeout(dndBadgeTimer);

  // Remove click listener
  const welcomeElement = document.querySelector('.welcome-text');
  if (welcomeElement) {
    welcomeElement.removeEventListener('click', toggleDebug);
  }

  // Remove visibility and focus listeners
  document.removeEventListener('visibilitychange', handleVisibilityChange);
  window.removeEventListener('focus', handleFocus);
  window.removeEventListener('pageshow', handlePageShow);
  window.removeEventListener('pixie-push-restored', onPushRestored);

  disconnectChatBadgeSocket();
  releaseMicrophone();
});
</script>

<style scoped>
.protected-page {
  display: flex;
  flex-direction: column;
  align-items: center;
  height: 100vh;
  max-width: 440px;
  width: 100%;
  padding: 0;
  box-sizing: border-box;
  background: #eee;
  touch-action: none;
  overflow: hidden;
  position: relative;
}

.content-container {
  display: flex;
  align-items: center; /* Changed from 'top' to 'center' for vertical centering */
  justify-content: center;
  width: 100%;
  padding-bottom: 30px;
  height: 95vh;
  overflow-y: hidden;
  margin-top: -5px;
  touch-action: none;
}

.tile-container {
  margin-top: 39px;
  display: grid;
  grid-template-columns: repeat(1, 1fr);
  grid-gap: 7px; /* Added some gap between tiles */
  padding: 20px;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  touch-action: pan-y;
  overscroll-behavior: contain;
  align-content: center; /* Added to center grid content vertically */
  padding-right: 20px;
  width: 96%;

  /* Hide scrollbar but keep it functional */
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE and Edge */
}

/* Hide scrollbar for Chrome, Safari and Opera */
.tile-container::-webkit-scrollbar {
  display: none;
}

/* Media query for 915px height devices */
@media screen and (height: 915px) {
  .tile-container {
    grid-gap: 12px; /* Adjusted gap for 915px tall devices */
  }
}

/* Media query for 812px height devices */
@media screen and (height: 812px) {
  .tile-container {
    margin-top: 32px; /* Adjusted margin for iPhone X/11/12 sized devices */
    /* padding-top: 30px; */
    grid-gap: 6px;
  }
}

.tile {
  background-color: #947797;
  border: 1px solid #a28ba5;
  display: flex;
  align-items: center;
  height: 51px;
  position: relative;
  cursor: pointer;
  box-shadow: 6px 5px 5px 0px rgba(0, 0, 0, 0.22);
  text-decoration: none;
  padding: 0 10px;
  box-sizing: border-box;
  border-radius: 5px;
  -webkit-tap-highlight-color: rgba(0, 0, 0, 0.1);

  /* Animation properties */
  animation: flyIn 0.5s ease-out forwards;
  opacity: 0;
  transform: translateY(-50px);
}

/* Define fly-in animation */
@keyframes flyIn {
  from {
    opacity: 0;
    transform: translateY(-50px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.tile-icon {
  border-top-left-radius: 4px;
  border-bottom-left-radius: 4px;
  width: 25%;
  height: 49px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 32px;
  color: #a58abd;
  border: 1pt solid #555555;
  margin-left: -11px;
  background: #675769;
}

.tile-link {
  width: 75%;
  color: #eee;
  text-align: center;
  font-size: 22px;
  font-weight: bold;
}

/*
 * Short iPhone / PWA viewports (e.g. 390×797). Default 51px tiles overflow
 * by a few pixels; shave 2px off each tile so the full grid fits.
 * Placed after base .tile rules so height overrides apply.
 */
@media screen and (max-height: 820px) {
  .tile-container {
    margin-top: 32px;
    grid-gap: 6px;
    padding-top: 16px;
    padding-bottom: 16px;
  }

  .tile {
    height: 49px;
  }

  .tile-icon {
    height: 47px;
  }
}

.router-link-active,
.router-link-exact-active {
  text-decoration: none; /* Ensure no underline when active */
}

.welcome {
  font-size: 19pt;
  width: 100%;
  padding: 10px;
  text-align: center;
  color: white;
  background: #675769;
  opacity: 0;
  animation: fadeIn 0.8s ease-out forwards;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  z-index: 40;
}

.welcome-text {
  text-align: center;
  /* width: 60%; */
}

/* Status dot styles */
.status-dot {
  width: 15px;
  height: 15px;
  border-radius: 50%;
  display: inline-block;
  position: absolute;
  left: 77px;
  transition: background-color 0.3s ease;
}

.status-green {
  background-color: #4caf50; /* Green */
}

.status-red {
  background-color: #f44336; /* Red */
}

/* Welcome fade-in animation */
@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.update-button {
  width: 90%;
  position: absolute;
  bottom: 10px;
  /* left: 50%; */
  /* transform: translateX(-50%); */
  padding: 10px 20px;
  /* background-color: #4caf50; */
  color: #c1bcbc;
  border: 1px solid #ddd;
  border-radius: 5px;
  cursor: pointer;
  margin-left: 4px;
  margin-right: 4px;
}

.version-footer {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding: 8px 20px 16px;
  width: 100%;
  box-sizing: border-box;
}

.version-btn {
  background: none;
  border: 1px solid #c4b8cb;
  color: #6e5875;
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 0.85em;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
}

.version-btn:active {
  background: #f0ecf2;
}

.version-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.update-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1100;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.update-card {
  background: #fff;
  border-radius: 16px;
  padding: 24px;
  max-width: 360px;
  width: 100%;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
}

.update-message {
  margin: 0 0 20px;
  font-size: 1em;
  color: #333;
  text-align: center;
  line-height: 1.5;
}

.update-fail-hint {
  margin: -8px 0 16px;
  padding: 10px 12px;
  font-size: 0.82em;
  line-height: 1.4;
  color: #6b3a00;
  background: #fff4e0;
  border: 1px solid #f0c36d;
  border-radius: 8px;
  text-align: left;
}

.update-actions {
  display: flex;
  gap: 10px;
}

.update-btn {
  flex: 1;
  padding: 12px 16px;
  border-radius: 10px;
  font-size: 1em;
  font-weight: 600;
  cursor: pointer;
  border: none;
}

.update-btn-full {
  width: 100%;
}

.update-btn-primary {
  background: #6e5875;
  color: #fff;
}

.update-btn-primary:active {
  background: #5a4762;
}

.update-btn-secondary {
  background: #fff;
  color: #6e5875;
  border: 1px solid #c4b8cb;
}

.update-btn-secondary:active {
  background: #f8f6f9;
}

.update-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.chat-content {
  font-size: 0.8em;
  margin-bottom: 5px;
  color: #888;
}

/* Add active state feedback for better mobile experience */
.tile:active {
  background-color: #675769;
  transform: translateY(1px);
}

/* Service unavailable popup styles */
.service-popup {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.service-popup-content {
  background-color: white;
  padding: 20px;
  border-radius: 5px;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.3);
  text-align: center;
  width: 86%;
  max-width: 360px;
  box-sizing: border-box;
}

.close-button {
  margin-top: 10px;
  padding: 8px 16px;
  background-color: #947797;
  width: 91%;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  position: relative;
  bottom: 5px;
  margin-bottom: 5px;
  /* transform: translateX(-50%); */
  margin-top: 5px;
}

.close-button:hover {
  background-color: #675769;
}

/* Log message styles */
.log-message {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 8px;
  border-radius: 5px;
  width: 80%;
  max-width: 600px;
  min-height: 40vh;
  font-size: 0.7em;
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 999;
  max-height: 50vh;
  background: #333333ee;
  color: #18fb18;
  box-shadow: 0 0 15px rgba(0, 0, 0, 0.5);
  animation: fadeIn 0.3s ease-in;
}

.log-message-content {
  flex: 1;
  overflow-y: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;
  margin-bottom: 10px;
  padding: 5px;
  line-height: 1.3em;
}

/* Hide scrollbar for Chrome, Safari and Opera */
.log-message-content::-webkit-scrollbar {
  display: none;
}

.log-message:not(:hover) {
  opacity: 0.9;
}

.clear-data-button {
  margin-bottom: 5px;
  height: 25px;
  width: 95%;
  border-radius: 5px;
  border: 1px solid #444;
  color: white;
  cursor: pointer;
  display: block;
  margin-left: auto;
  margin-right: auto;
}

.button-container {
  display: flex;
  justify-content: space-between;
  gap: 10px;
}

.data-button {
  flex: 1;
  padding: 8px;
  border: 1px solid #444;
  border-radius: 5px;
  background: rgba(128, 128, 128, 0.5);
  color: white;
  cursor: pointer;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 0.9;
  }
}

@keyframes fadeOut {
  from {
    opacity: 0.9;
  }
  to {
    opacity: 0;
  }
}

/* Settings popup styles */
.settings-popup {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.settings-popup-content {
  background-color: white;
  padding: 0;
  border-radius: 5px;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.3);
  text-align: center;
  width: 79%;
  max-width: 400px;
  position: relative;
  height: 75vh;
  margin: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.settings-popup-content--fluidbox {
  height: 92vh;
}

.settings-popup-content--fluidbox .settings-body {
  overflow: hidden;
  display: flex;
  flex-direction: column;
  min-height: 0;
  padding-bottom: 12px;
}

/* Mauve title bar — matches MotdModal / other mobile dialogs */
.settings-title-bar {
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  background: #675769;
  color: #fff;
  padding: 12px 40px;
  flex-shrink: 0;
}

.settings-title {
  font-size: 16pt;
  font-weight: 600;
  line-height: 1.2;
}

.settings-close-btn {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  width: 32px;
  height: 32px;
  border: none;
  background: transparent;
  color: #fff;
  font-size: 18px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  padding: 0;
}

.settings-close-btn:hover,
.settings-close-btn:active {
  background: rgba(255, 255, 255, 0.15);
}

.settings-body {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
  text-align: center;
}

.settings-button,
.close-button,
.cancel-button {
  padding: 12px 16px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 16px;
  height: 44px; /* Consistent height for all buttons */
  display: flex;
  align-items: center;
  justify-content: center;
}

.settings-button {
  background-color: #947797;
  color: white;
  width: 100%;
}

.cancel-button {
  background-color: #999; /* Grey instead of red */
  color: white;
}

.form-group {
  margin-bottom: 15px;
  text-align: left; /* Left align the form groups */
  padding: 0 10px 0 10px;
}

.form-group label {
  display: block;
  margin-bottom: 5px;
  font-weight: bold;
  text-align: left; /* Left align labels */
}

.form-group input,
.form-group select,
.form-group textarea {
  width: 100%;
  border: 1px solid #ddd;
  border-radius: 5px;
  padding: 10px;
  outline: none;
  box-sizing: border-box;
}

.action-buttons {
  display: flex;
  justify-content: space-between;
  width: 80%;
  margin: 0 auto;
  margin-bottom: 5px;
  gap: 10px;
  margin-top: 21px;
}

.action-buttons .settings-button,
.action-buttons .cancel-button {
  width: 100%;
  margin-top: 0;
}

.action-buttons-vertical {
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
  margin: 0 auto;
}

.action-buttons-vertical .settings-button {
  width: 100%;
  margin-top: 0;
}

.action-buttons-vertical .settings-button {
  width: 100%;
  margin: 5px;
}

/* Log capture button: white FA icons (no emoji) */
.log-capture-button {
  gap: 10px;
}

.log-capture-icon {
  color: #fff;
  font-size: 1.05em;
  line-height: 1;
}

.settings-button.log-capture-active {
  background-color: #d32f2f;
  color: white;
  font-weight: bold;
}

.settings-button.log-capture-active .log-capture-icon {
  color: #fff;
  animation: log-capture-pulse 1.4s ease-in-out infinite;
}

@keyframes log-capture-pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.45;
  }
}

.password-change-form {
  margin-top: 20px;
}

.password-message {
  margin-top: 10px;
  font-size: 0.9em;
}

.notification-status {
  font-size: 0.85em;
  color: #555;
  margin-top: -2px;
}

.settings-divider {
  border: none;
  border-top: 1px solid #ddd;
  margin: 8px 8px 4px;
  width: calc(100% - 16px);
}

.password-message.error {
  color: #f44336;
}

.motd-editor-form {
  margin-top: 20px;
  height: 100vh;
  position: absolute;
  top: 20px;
  background: #fff;
  z-index: 10000;
  width: 100%;
  height: 50vh;
  left: 0%;
}

.preview-section {
  margin-top: 15px;
}

.markdown-preview {
  border: 1px solid #ddd;
  border-radius: 5px;
  padding: 10px;
  background-color: #f9f9f9;
  max-height: 80px;
  overflow-y: auto;
  min-height: 80px;
  width: 87%;
  margin-left: 3%;
  font-size: 0.7em;
}

.motd-message {
  margin-top: 10px;
  font-size: 0.9em;
}

.motd-message.error {
  color: #f44336;
}

.settings-icon {
  position: absolute;
  left: 42px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 20px;
  cursor: pointer;
  z-index: 10;
  color: white;
}

.pixie-daily-icon {
  position: absolute;
  right: 72px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 22px;
  line-height: 1;
  cursor: pointer;
  z-index: 10;
  color: white;
  background: transparent;
  border: 0;
  margin: 0;
  padding: 12px;
}

.microphone-icon {
  position: absolute;
  right: 25px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 32px;
  cursor: pointer;
  z-index: 10;
  color: white;
  padding: 12px;
}

.home-mic-hint {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 30;
  background: rgba(20, 16, 24, 0.82);
}

.home-mic-hint-callout {
  position: absolute;
  top: var(--home-mic-hint-top, 0px);
  right: var(--home-mic-arrow-right, 13px);
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  max-width: min(280px, calc(100vw - 48px));
  color: #fff;
  pointer-events: none;
}

.home-mic-hint-arrow {
  width: 40px;
  height: 64px;
  overflow: visible;
  filter: drop-shadow(0 1px 1px rgba(0, 0, 0, 0.35));
}

.home-mic-hint-text {
  margin: 8px 0 0;
  color: #fff;
  font-size: 1.05em;
  font-weight: 700;
  line-height: 1.35;
  text-align: right;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.45);
}

.home-mic-hint-dismiss {
  pointer-events: auto;
  margin-top: 1.6em;
  min-height: 44px;
  padding: 12px 14px;
  border: 1px solid rgba(255, 255, 255, 0.75);
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.16);
  color: #fff;
  font-size: 0.85em;
  font-weight: 600;
  line-height: 1.2;
  cursor: pointer;
}

.voice-mic-overlay {
  position: absolute;
  inset: 0;
  z-index: 4000;
  background: rgba(20, 16, 24, 0.94);
  display: flex;
  align-items: center;
  justify-content: center;
}

.voice-mic-overlay-office {
  background: rgba(27, 94, 42, 0.88);
}

.voice-mic-stack {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 28px;
}

.voice-mic-grid {
  display: grid;
  grid-template-columns: repeat(2, 104px);
  gap: 56px;
  justify-items: center;
}

.voice-mic-grid button {
  width: 104px;
  height: 104px;
  border-radius: 50%;
  border: none;
  font-size: 40px;
  color: #fff;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  padding: 0;
  line-height: 1;
}

.voice-mic-label {
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.02em;
  line-height: 1;
}

.voice-mic-pause {
  background: #5c4d62;
}

.voice-mic-send {
  background: #6e5875;
}

.voice-mic-delete {
  background: #8d4d55;
}

.voice-mic-exit {
  background: #5c4d62;
}

.voice-mic-office {
  width: 264px;
  min-height: 76px;
  border: 2px solid rgba(255, 255, 255, 0.45);
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.14);
  color: #fff;
  font-size: 22px;
  font-weight: 700;
  letter-spacing: 0.02em;
}

.voice-mic-office-on {
  background: #14532d;
  border-color: #fff;
  box-shadow: inset 0 0 0 3px #fff;
}

.voice-mic-send:disabled {
  opacity: 0.6;
}

/* Usage Info Styles */
.usage-info-form {
  margin-top: 20px;
  max-height: 50vh;
  overflow-y: auto;
}

.usage-entries-container {
  margin-top: 15px;
  max-height: 30vh;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch; /* Smooth scrolling on iOS */
}

.usage-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.85em;
}

.usage-table th,
.usage-table td {
  border: 1px solid #ddd;
  padding: 6px;
  text-align: left;
}

.usage-table th {
  background-color: #f2f2f2;
  font-weight: bold;
  position: sticky;
  top: 0;
}

.usage-table tr:nth-child(even) {
  background-color: #f9f9f9;
}

.usage-table tr:hover {
  background-color: #eaeaea;
}

/* Usage Info Popup Styles */
.usage-info-popup {
  position: fixed;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.usage-info-popup-content {
  background-color: white;
  padding: 20px;
  border-radius: 5px;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.3);
  text-align: center;
  width: 75%;
  max-width: 400px;
  position: relative;
  padding-bottom: 50px; /* Add space at the bottom for fixed close button */
  height: 50vh;
}
tbody {
  font-size: 0.8em;
}

/* Calendar badge styles */
.calendar-badge {
  position: absolute;
  top: 5px;
  right: 5px;
  background: #dc3545;
  color: white;
  border-radius: 50%;
  padding: 0;
  font-size: 10px;
  font-weight: bold;
  width: 18px;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid white;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  line-height: 1;
  z-index: 10;
}

/* Work orders badge styles */
.work-orders-badge {
  position: absolute;
  top: 5px;
  right: 5px;
  background: #dc3545;
  color: white;
  border-radius: 50%;
  padding: 0;
  font-size: 10px;
  font-weight: bold;
  width: 18px;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid white;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  line-height: 1;
  z-index: 10;
}

/* Email badge styles */
.email-badge {
  position: absolute;
  top: 5px;
  right: 5px;
  background: #dc3545;
  color: white;
  border-radius: 50%;
  padding: 0;
  font-size: 10px;
  font-weight: bold;
  width: 18px;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid white;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  line-height: 1;
  z-index: 10;
}

.sms-badge {
  position: absolute;
  top: 5px;
  right: 5px;
  background: #dc3545;
  color: white;
  border-radius: 50%;
  padding: 0;
  font-size: 10px;
  font-weight: bold;
  width: 18px;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid white;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  line-height: 1;
  z-index: 10;
}

/* Chat Bot badge styles */
.chat-bot-badge {
  position: absolute;
  top: 5px;
  right: 5px;
  background: #dc3545;
  color: white;
  border-radius: 50%;
  padding: 0;
  font-size: 10px;
  font-weight: bold;
  width: 18px;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid white;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  line-height: 1;
  z-index: 10;
}

/* Mobile-specific overrides */
@media screen and (max-width: 440px) {
  .motd-editor-form {
    margin-top: 20px;
    height: 100vh;
    position: absolute;
    top: 22px;
    background: #fff;
    z-index: 10000;
    width: 100%;
    height: 50vh;
    left: 0%;
  }
}
</style>
